import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import {
  fetchHospitals,
  setSelectedHospital,
  setTenantSwitching,
  verifyUserAccess,
} from '../store/hospitalSlice';
import { setCurrentHospitalRoles } from '../store/userSlice';
import { ensureTenant } from '../services/auth/brokerSession';
import { Hospital } from '../types/api';

export const useHospitals = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    hospitals,
    selectedHospital,
    loading,
    error,
    switchingTenant,
    userVerificationLoading,
    userVerificationError,
  } = useSelector((state: RootState) => state.hospitals);

  const { user } = useSelector((state: RootState) => state.user);

  const lastVerifiedHospitalId = useRef<string | null>(null);
  const autoSelectInitiated = useRef(false);

  // Fetch hospitals on mount only
  useEffect(() => {
    dispatch(fetchHospitals());
  }, [dispatch]);

  // Memoized verification function to prevent unnecessary re-renders
  const verifyUserAccessCallback = useCallback(
    (tenantId: string) => {
      dispatch(verifyUserAccess(tenantId));
    },
    [dispatch],
  );

  // Trigger user verification when a hospital is auto-selected or newly selected
  useEffect(() => {
    if (
      selectedHospital &&
      !userVerificationLoading &&
      !userVerificationError &&
      lastVerifiedHospitalId.current !== selectedHospital.tenantId
    ) {
      lastVerifiedHospitalId.current = selectedHospital.tenantId;
      verifyUserAccessCallback(selectedHospital.tenantId);
    }
  }, [
    selectedHospital,
    userVerificationLoading,
    userVerificationError,
    verifyUserAccessCallback,
  ]);

  const selectHospital = useCallback(
    async (hospital: Hospital | null) => {
      if (!hospital) {
        dispatch(setSelectedHospital(null));
        return;
      }

      // Switch the broker token to this tenant BEFORE publishing the selection.
      // Tenant-scoped data fetches key off selectedHospital, so publishing first
      // would let them fire against a token still scoped to the previous tenant —
      // which attachMaiaAuth rejects as a tenant mismatch. The first switch to a
      // given tenant is a real broker round-trip (no cached session yet), which
      // is exactly when that race surfaced. Awaiting the switch here closes it
      // for every tenant-scoped consumer, not just the dashboard.
      //
      // `switchingTenant` disables the dropdown for the whole switch; it stays
      // set through the verify that follows and is cleared by verifyUserAccess.
      dispatch(setTenantSwitching(true));
      try {
        await ensureTenant(hospital.tenantId);
      } catch (error) {
        dispatch(setTenantSwitching(false));
        throw error;
      }
      dispatch(setSelectedHospital(hospital));

      // Update current hospital roles based on the selected hospital
      if (user && user.tenants) {
        const tenant = user.tenants.find(
          (t) => t.tenantId === hospital.tenantId,
        );

        if (tenant) {
          dispatch(
            setCurrentHospitalRoles({
              roles: tenant.roles,
              permissions: tenant.permissions,
            }),
          );
        }
      }
    },
    [dispatch, user],
  );

  // Auto-select the first tenant once hospitals load and nothing is selected
  // (fresh sign-in). Routed through selectHospital so the broker token is
  // switched to it before the selection is published — the same guarantee the
  // manual path gets, so the initial dashboard load can't race the default
  // token either. Guarded by a ref so the async switch can't re-trigger it.
  useEffect(() => {
    if (autoSelectInitiated.current || selectedHospital || !hospitals?.length) {
      return;
    }
    autoSelectInitiated.current = true;
    void selectHospital(hospitals[0]).catch(() => {
      autoSelectInitiated.current = false;
    });
  }, [selectedHospital, hospitals, selectHospital]);

  const refreshHospitals = useCallback(() => {
    dispatch(fetchHospitals());
  }, [dispatch]);

  // Memoize the return value to prevent unnecessary re-renders
  const result = useMemo(
    () => ({
      hospitals,
      selectedHospital,
      loading,
      error,
      switchingTenant,
      userVerificationLoading,
      userVerificationError,
      selectHospital,
      refreshHospitals,
      hasMultipleHospitals: (hospitals?.length ?? 0) > 1,
    }),
    [
      hospitals,
      selectedHospital,
      loading,
      error,
      switchingTenant,
      userVerificationLoading,
      userVerificationError,
      selectHospital,
      refreshHospitals,
    ],
  );

  return result;
};
